import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon, ArrowDownTrayIcon, ArrowPathIcon,
  MapPinIcon, UserIcon, SparklesIcon, ClockIcon,
  CheckCircleIcon, ExclamationCircleIcon, Squares2X2Icon,
  FolderIcon, BuildingOffice2Icon,
  PauseIcon, PlayIcon, XMarkIcon, PauseCircleIcon, NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { BoltIcon } from '@heroicons/react/24/solid';
import { toast } from 'sonner';
import { apiClient } from '../../lib/axios';
import { downloadBlob } from '../../lib/downloadBlob';
import { DataTable, Column, PaginationMeta } from '../../components/ui/DataTable';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { subscribeToUserChannel, ProgressEvent } from '../../lib/echo';
import { useAuthStore } from '../../store/useAuthStore';

interface SearchTask {
  id: string;
  type: string;
  company: string;
  location?: string;
  search_titles?: string | string[];
  depth?: number;
  status: string;
  progress: number;
  total_records: number;
  processed_records: number;
  leads_found?: number;
  results: string | null;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  credits_used: number;
  cached_count?: number;
}

interface Lead {
  name: string;
  title: string;
  company_name: string;
  company_domain?: string;
}

const DEFAULT_META: PaginationMeta = { total: 0, page: 1, per_page: 20, last_page: 1 };

const DEPTH_LABELS: Record<number, string> = {
  1: 'Normal Search',
  2: 'Deep Search ×1',
  3: 'Deep Search ×3',
  4: 'Deep Search ×4',
  5: 'Deep Search ×5',
};

const clean = (val?: string) => (!val || val.toLowerCase() === 'unknown' ? '' : val);

const LEAD_COLUMNS: Column<Lead>[] = [
  {
    key: 'name',
    header: 'Name',
    width: '180px',
    render: (val) => <span className="font-bold text-slate-900">{clean(val) || '—'}</span>,
  },
  { key: 'title', header: 'Title', width: '160px', render: (val) => clean(val) || '' },
  {
    key: 'company_name',
    header: 'Company',
    width: '160px',
    render: (val) => (
      <div className="flex items-center gap-1.5">
        <BuildingOffice2Icon className="w-3.5 h-3.5 text-slate-500 shrink-0" />
        <span>{clean(val) || '—'}</span>
      </div>
    ),
  },
  {
    key: 'company_domain',
    header: 'Domain',
    width: '150px',
    render: (val) => {
      const d = clean(val);
      return d
        ? <a href={`https://${d}`} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline text-xs font-semibold">{d}</a>
        : <span className="text-slate-500">—</span>;
    },
  },
];

export default function SearchHistoryDetail() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [task, setTask] = useState<SearchTask | null>(null);
  const [taskLoading, setTaskLoading] = useState(true);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadsMeta, setLeadsMeta] = useState<PaginationMeta>(DEFAULT_META);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [jobAction, setJobAction] = useState<'pause' | 'resume' | 'cancel' | null>(null);
  const [pollTick, setPollTick] = useState(0);
  const taskRef = useRef<SearchTask | null>(null);
  // Tracks completed companies via WebSocket only — not overwritten by polling
  const [completedCompanies, setCompletedCompanies] = useState<number | null>(null);

  useDocumentTitle(task ? task.company : 'Search Detail', 'View results and parameters for this search.');

  const fetchTask = useCallback(async () => {
    if (!taskId) return;
    try {
      const res = await apiClient.get(`/search/history/${taskId}`);
      const fetched = res.data as SearchTask;
      setTask(fetched);
      taskRef.current = fetched;
      return fetched;
    } catch {
      navigate('/dashboard/search_history');
    } finally {
      setTaskLoading(false);
    }
  }, [taskId, navigate]);

  const fetchLeads = useCallback(async (page: number, currentTask?: SearchTask | null) => {
    const t = currentTask ?? taskRef.current;
    if (!t || !['completed', 'cancelled'].includes(t.status)) return;
    setLeadsLoading(true);
    try {
      const endpoint = t.type === 'bulk'
        ? `/search/bulk/results/${t.id}`
        : `/search/tasks/${t.id}/results`;
      const res = await apiClient.get(endpoint, { params: { page, per_page: 20 } });
      setLeads(res.data.data ?? []);
      setLeadsMeta(res.data.meta ?? DEFAULT_META);
    } catch {
      setLeads([]);
    } finally {
      setLeadsLoading(false);
    }
  }, []);

  // Initial load + polling while running
  useEffect(() => {
    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;

    const STOPPED = ['completed', 'failed', 'paused', 'cancelled'];
    const load = async () => {
      const fetched = await fetchTask();
      if (cancelled) return;
      if (fetched && !STOPPED.includes(fetched.status)) {
        interval = setInterval(async () => {
          if (cancelled) return;
          const updated = await fetchTask();
          if (updated && STOPPED.includes(updated.status)) {
            if (interval) clearInterval(interval);
            if (updated.status === 'completed' || updated.status === 'cancelled') fetchLeads(1, updated);
          }
        }, 5000);
      } else if (fetched && (fetched.status === 'completed' || fetched.status === 'cancelled')) {
        fetchLeads(1, fetched);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [fetchTask, fetchLeads, pollTick]);

  // WebSocket real-time updates
  useEffect(() => {
    if (!user || !taskId) return;
    const unsubscribe = subscribeToUserChannel(user.id, (e: ProgressEvent) => {
      if (e.task_id !== taskId) return;

      if (e.status === 'completed' || e.status === 'failed') {
        fetchTask().then((updated) => {
          if (updated?.status === 'completed') fetchLeads(1, updated);
        });
        return;
      }

      // During processing: e.leads_found = $completedCount (charged + cached-free companies)
      if (e.leads_found !== undefined && e.leads_found !== null) {
        setCompletedCompanies(e.leads_found);
      }
      setTask(prev => {
        if (!prev) return prev;
        const pct = prev.total_records > 0
          ? Math.min(98, Math.round(((e.leads_found ?? prev.processed_records) / prev.total_records) * 100))
          : e.progress;
        const updated = {
          ...prev,
          status: e.status,
          progress: pct,
          processed_records: e.leads_found ?? prev.processed_records,
        };
        taskRef.current = updated;
        return updated;
      });
    });
    return unsubscribe;
  }, [user?.id, taskId, fetchTask, fetchLeads]);

  const handleDownload = async () => {
    if (!task || isDownloading) return;
    setIsDownloading(true);
    try {
      const ts = new Date().toISOString().slice(0, 19).replace(/[-T:]/g, (c) => (c === 'T' ? '_' : c));
      await downloadBlob(`/search/export/download/${task.id}`, `${task.company}_${ts}_export.csv`);
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const runJobAction = async (action: 'pause' | 'resume' | 'cancel') => {
    if (!task || jobAction) return;
    if (action === 'cancel' && !window.confirm('Cancel this batch? Companies already processed stay available, but the rest will be skipped.')) return;
    setJobAction(action);
    try {
      await apiClient.post(`/search/bulk/tasks/${task.id}/${action}`);
      const nextStatus = action === 'pause' ? 'paused' : action === 'cancel' ? 'cancelled' : 'processing';
      setTask(prev => {
        if (!prev) return prev;
        const updated = { ...prev, status: nextStatus };
        taskRef.current = updated;
        return updated;
      });
      toast.success(action === 'pause' ? 'Job paused.' : action === 'resume' ? 'Job resumed.' : 'Job cancelled.');
      // Re-sync from server (resume restarts the worker; pause/cancel finalize progress)
      const refreshed = await fetchTask();
      if (action === 'resume') setPollTick(t => t + 1);
      if (action === 'cancel') fetchLeads(1, refreshed);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? `Failed to ${action} job.`);
    } finally {
      setJobAction(null);
    }
  };

  const parseTitles = (d?: string | string[]): string[] => {
    if (!d) return [];
    if (Array.isArray(d)) return d;
    try { return JSON.parse(d); } catch { return []; }
  };

  const formatDate = (s: string) =>
    new Date(s).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const formatDuration = (start?: string | null, end?: string | null): string | null => {
    if (!start || !end) return null;
    const secs = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000);
    if (secs < 60) return `${secs}s`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
    return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
  };

  const getPeopleFound = () => {
    if (!task) return 0;
    // leads_found is the canonical, de-duplicated count the backend returns for
    // both bulk and single searches — keep it as the single source of truth so
    // the header stat always matches the "N found" results badge.
    if (task.leads_found !== undefined && task.leads_found !== null) return task.leads_found;
    if (leadsMeta.total > 0) return leadsMeta.total;
    if (task.results) {
      try {
        const parsed = JSON.parse(task.results);
        if (typeof parsed.count === 'number') return parsed.count;
        if (Array.isArray(parsed)) return parsed.length;
      } catch {}
    }
    return 0;
  };

  if (taskLoading) {
    return (
      <div className="h-full flex flex-col bg-slate-50/50">
        <div className="px-4 sm:px-8 py-6 flex flex-col gap-4">
          {[300, 200, 400, 600].map((w, i) => (
            <div key={i} className="h-4 bg-slate-100 rounded" style={{ width: w }} />
          ))}
        </div>
      </div>
    );
  }

  if (!task) return null;

  const isCompleted = task.status === 'completed';
  const isFailed = task.status === 'failed';
  const isPaused = task.status === 'paused';
  const isCancelled = task.status === 'cancelled';
  const isRunning = !isCompleted && !isFailed && !isPaused && !isCancelled;

  const progressPct = task.total_records > 0
    ? Math.min(98, Math.round((task.processed_records / task.total_records) * 100))
    : Math.max(1, task.progress ?? 1);

  const peopleFound = getPeopleFound();
  const isBulk = task.type === 'bulk';

  // Single source of truth for "companies done" so the header stat, status pill and
  // the processing card never disagree. Prefer the live WebSocket count (charged +
  // cached-free companies); fall back to processed_records from polling. Never derive
  // it from credits_used/depth — that undercounts cached-free companies.
  const companiesDone = completedCompanies !== null ? completedCompanies : task.processed_records;

  return (
    <div className="h-full flex flex-col bg-slate-50/50">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .fade-in { animation: fadeIn 0.2s ease-out both; }
        @keyframes slide { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
        .animate-slide { animation: slide 1.5s infinite linear; }
      `}</style>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-4 sm:py-8 flex flex-col gap-6">

          {/* Top bar */}
          <div className="flex items-center justify-between fade-in">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard/search_history"
                className="flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-slate-800 transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4" /> Back
              </Link>
              <span className="text-slate-400 text-lg">|</span>
              <span className="text-sm font-bold text-slate-400">
                {isBulk ? 'Bulk Upload' : 'Single Search'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {isBulk && (isRunning || isPaused) && (
                <>
                  {isPaused ? (
                    <button
                      onClick={() => runJobAction('resume')}
                      disabled={jobAction !== null}
                      className="flex items-center gap-1.5 bg-brand-500 text-slate-900 px-3 sm:px-4 py-2 rounded-xl text-sm font-bold hover:bg-brand-600 transition-colors disabled:opacity-60 whitespace-nowrap"
                    >
                      {jobAction === 'resume' ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <PlayIcon className="w-3.5 h-3.5" />}
                      <span className="hidden sm:inline">Resume</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => runJobAction('pause')}
                      disabled={jobAction !== null}
                      className="flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 px-3 sm:px-4 py-2 rounded-xl text-sm font-bold hover:bg-slate-50 transition-colors disabled:opacity-60 whitespace-nowrap"
                    >
                      {jobAction === 'pause' ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <PauseIcon className="w-3.5 h-3.5" />}
                      <span className="hidden sm:inline">Pause</span>
                    </button>
                  )}
                  <button
                    onClick={() => runJobAction('cancel')}
                    disabled={jobAction !== null}
                    className="flex items-center gap-1.5 bg-white border border-red-200 text-red-600 px-3 sm:px-4 py-2 rounded-xl text-sm font-bold hover:bg-red-50 transition-colors disabled:opacity-60 whitespace-nowrap"
                  >
                    {jobAction === 'cancel' ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <XMarkIcon className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">Cancel</span>
                  </button>
                </>
              )}
              {(isCompleted || (isCancelled && leadsMeta.total > 0)) && (
                <button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="flex items-center gap-1.5 bg-brand-500 text-slate-900 px-3 sm:px-5 py-2 rounded-xl text-sm font-bold hover:bg-brand-600 transition-colors shadow-sm shadow-brand-500/20 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isDownloading
                    ? <><ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /><span className="hidden sm:inline"> Downloading...</span></>
                    : <><ArrowDownTrayIcon className="w-3.5 h-3.5" /><span className="hidden sm:inline"> Export CSV</span></>
                  }
                </button>
              )}
            </div>
          </div>

          {/* Hero card */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden fade-in">
            <div className="p-6 border-b border-slate-100">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Title block */}
                <div className="lg:col-span-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                      isBulk ? 'bg-slate-100 text-slate-600' : 'bg-brand-50 text-slate-800'
                    }`}>
                      {isBulk ? <FolderIcon className="w-4 h-4" /> : <Squares2X2Icon className="w-4 h-4" />}
                    </div>
                    <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                      {DEPTH_LABELS[task.depth ?? 1] ?? `Depth ×${task.depth}`}
                    </span>
                  </div>
                  <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-1">{task.company}</h1>
                  <p className="text-[12px] text-slate-400 font-medium">{formatDate(task.created_at)}</p>
                </div>

                {/* Stats block */}
                <div className={`lg:col-span-6 grid gap-4 lg:border-l lg:border-slate-100 lg:pl-6 ${isBulk ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3'}`}>
                  {/* Status */}
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Status</p>
                    {isCompleted && (
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full">
                        <CheckCircleIcon className="w-3.5 h-3.5" /> Ready
                      </div>
                    )}
                    {isFailed && (
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-red-600 text-[10px] font-bold rounded-full">
                        <ExclamationCircleIcon className="w-3.5 h-3.5" /> Failed
                      </div>
                    )}
                    {isPaused && (
                      <div>
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-600 text-[10px] font-bold rounded-full mb-2">
                          <PauseCircleIcon className="w-3.5 h-3.5" />
                          {isBulk && task.total_records > 0
                            ? `${companiesDone.toLocaleString()} / ${task.total_records.toLocaleString()}`
                            : 'Paused'}
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
                        </div>
                      </div>
                    )}
                    {isCancelled && (
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-500 text-[10px] font-bold rounded-full">
                        <NoSymbolIcon className="w-3.5 h-3.5" /> Cancelled
                      </div>
                    )}
                    {isRunning && (
                      <div>
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-full mb-2">
                          <ArrowPathIcon className="w-3 h-3 animate-spin" />
                          {isBulk && task.total_records > 0
                            ? `${companiesDone.toLocaleString()} / ${task.total_records.toLocaleString()}`
                            : `${progressPct}%`
                          }
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all duration-500"
                            style={{ width: `${isCompleted ? 100 : progressPct}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* People Found */}
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">People Found</p>
                    <p className="text-2xl font-extrabold text-slate-900 leading-tight mt-1">{peopleFound.toLocaleString()}</p>
                  </div>

                  {/* Companies Processed (bulk only) */}
                  {isBulk && (
                    <div>
                      <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">Companies</p>
                      <p className="text-2xl font-extrabold text-slate-900 leading-tight mt-1">
                        {companiesDone.toLocaleString()}
                        {task.total_records > 0 && (
                          <span className="text-xs font-medium text-slate-400 ml-1">/ {task.total_records.toLocaleString()}</span>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Credits */}
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">Credits</p>
                    <div className="flex items-center gap-1 mt-2">
                      <BoltIcon className="w-3.5 h-3.5 text-brand-400" />
                      <p className="text-xl font-extrabold text-brand-700 leading-tight">{(task.credits_used ?? 0).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Parameters strip */}
            <div className="px-6 py-4 bg-slate-50 flex flex-wrap items-start gap-6">
              {task.type === 'single' && task.location && (
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1">
                    <MapPinIcon className="w-3 h-3" /> Location
                  </p>
                  <p className="text-[13px] font-bold text-slate-800">{task.location}</p>
                </div>
              )}
              {parseTitles(task.search_titles).length > 0 && (
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-2">
                    <UserIcon className="w-3 h-3" /> Roles Searched
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {parseTitles(task.search_titles).map((t, i) => (
                      <span key={i} className="bg-brand-50 text-brand-700 border border-brand-200 px-2.5 py-1 text-[11px] font-bold rounded-full">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {task.depth && (
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-2">
                    <SparklesIcon className="w-3 h-3" /> Depth
                  </p>
                  <span className="bg-brand-50 text-brand-700 border border-brand-200 px-2.5 py-1 text-[11px] font-bold rounded-full">
                    {DEPTH_LABELS[task.depth] ?? `×${task.depth}`}
                  </span>
                </div>
              )}
              {formatDuration(task.started_at, task.completed_at) && (
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1">
                    <ClockIcon className="w-3 h-3" /> Duration
                  </p>
                  <p className="text-[13px] font-bold text-slate-800">{formatDuration(task.started_at, task.completed_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Results section */}
          <div className="flex flex-col gap-3 fade-in">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wide">Results</span>
                {(isCompleted || isCancelled) && leadsMeta.total > 0 && (
                  <span className="bg-brand-500 text-slate-900 px-2 py-0.5 text-[10px] font-bold rounded-full">
                    {leadsMeta.total.toLocaleString()} found{isCancelled ? ' (partial)' : ''}
                  </span>
                )}
              </div>
              {(isCompleted || isCancelled) && leadsMeta.total > 0 && (
                <button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="flex items-center gap-1.5 bg-brand-500 text-slate-900 px-3 sm:px-4 py-2 rounded-xl text-sm font-bold hover:bg-brand-600 transition-colors shadow-sm shadow-brand-500/20 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isDownloading
                    ? <><ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /><span className="hidden sm:inline"> Downloading...</span></>
                    : <><ArrowDownTrayIcon className="w-3.5 h-3.5" /><span className="hidden sm:inline"> Export CSV</span></>
                  }
                </button>
              )}
            </div>

            {isBulk && !isRunning && (task.cached_count ?? 0) > 0 && (
              <div className="flex items-start gap-2.5 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl">
                <SparklesIcon className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                <p className="text-[12px] text-blue-700 leading-relaxed">
                  <span className="font-bold">{(task.cached_count ?? 0).toLocaleString()} rows were skipped</span> because they were run in the last 30 days (same company &amp; address), reused free, not charged.
                </p>
              </div>
            )}

            {isRunning ? (
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm py-16 text-center">
                <div className="relative w-12 h-12 mx-auto mb-4">
                  <svg className="w-12 h-12 animate-spin text-blue-200" viewBox="0 0 48 48" fill="none">
                    <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="4" />
                  </svg>
                  <svg className="w-12 h-12 absolute inset-0 animate-spin text-blue-500" style={{ animationDuration: '0.8s' }} viewBox="0 0 48 48" fill="none">
                    <path d="M44 24c0-11.046-8.954-20-20-20" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                  </svg>
                </div>
                <p className="text-sm font-bold text-slate-700 mb-1">Processing your search…</p>
                {isBulk && task.total_records > 0 ? (
                  <p className="text-[12px] text-slate-400">
                    {companiesDone.toLocaleString()} of {task.total_records.toLocaleString()} companies done · {progressPct}% complete
                  </p>
                ) : (
                  <p className="text-[12px] text-slate-400">Results will appear here automatically when finished.</p>
                )}
                <div className="w-48 h-1.5 bg-slate-100 rounded-full overflow-hidden mx-auto mt-4">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-700"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            ) : isFailed ? (
              <div className="bg-red-50 border border-red-100 rounded-2xl py-16 text-center">
                <ExclamationCircleIcon className="w-8 h-8 text-red-300 mx-auto mb-3" />
                <p className="text-sm font-bold text-red-600">Search Failed</p>
                <p className="text-[12px] text-red-400 mt-1">Credits were not charged. Try again from the Search page.</p>
                <Link to="/dashboard/search" className="mt-4 inline-flex items-center gap-2 bg-brand-500 text-slate-900 px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-brand-600 transition-colors">
                  New Search
                </Link>
              </div>
            ) : isPaused ? (
              <div className="bg-amber-50/60 border border-amber-100 rounded-2xl py-16 text-center">
                <PauseCircleIcon className="w-9 h-9 text-amber-400 mx-auto mb-3" />
                <p className="text-sm font-bold text-slate-700">Job Paused</p>
                <p className="text-[12px] text-slate-500 mt-1">
                  {isBulk && task.total_records > 0
                    ? `Paused at ${companiesDone.toLocaleString()} of ${task.total_records.toLocaleString()} companies. Resume to continue where it left off.`
                    : 'Resume to continue this job.'}
                </p>
                <button
                  onClick={() => runJobAction('resume')}
                  disabled={jobAction !== null}
                  className="mt-4 inline-flex items-center gap-2 bg-brand-500 text-slate-900 px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-brand-600 transition-colors disabled:opacity-60"
                >
                  {jobAction === 'resume' ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <PlayIcon className="w-4 h-4" />} Resume Job
                </button>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                {isCancelled && (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                    <NoSymbolIcon className="w-4 h-4 text-slate-400 shrink-0" />
                    <p className="text-[12px] text-slate-500">
                      Job cancelled{isBulk && task.total_records > 0 ? ` after ${companiesDone.toLocaleString()} of ${task.total_records.toLocaleString()} companies` : ''}. Showing partial results.
                    </p>
                  </div>
                )}
                <DataTable<Lead>
                  columns={LEAD_COLUMNS}
                  data={leads}
                  meta={leadsMeta}
                  onPageChange={(page) => fetchLeads(page)}
                  isLoading={leadsLoading}
                  emptyMessage={isCancelled ? "No results were produced before the job was cancelled." : "No decision makers were found for this search."}
                />
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
